import { Button, InputNumber, Select, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import LOCALS from 'commons/locals';
import {
  CHECKOUT_OUT_PRODUCT_PRICE_TYPE_OPTION_LIST,
  CHECKOUT_OUT_PRODUCT_PRICE_TYPE,
  findLabelByValue,
  PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
  PRODUCT_STOCK_TYPE,
} from 'commons/options';
import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { PmsProductWithPriceType } from 'types/pms';
import i18n from '../../../i18n';
import { StoreConfirmOrder } from 'types/oms';
import { STOCK_PLACE_OPTION_LIST } from 'constant/stock-place';
import { PUBLISH_STATUS_OPTION_LIST } from 'constant/pms-product';

const ProductTable = ({
  loading,
  productList,
  onChange,
  onDelete,
  mode,
  orderResult,
}: {
  loading: boolean;
  productList: PmsProductWithPriceType[];
  onChange?: (product: PmsProductWithPriceType) => void;
  onDelete?: (product: PmsProductWithPriceType) => void;
  mode: 'list' | 'modal'; // 使用的场景
  orderResult?: StoreConfirmOrder;
}) => {
  const getColumns = useMemo(() => {
    let columns: ColumnsType<PmsProductWithPriceType> = [
      {
        title: i18n.t(LOCALS.product_sn),
        dataIndex: 'id',
        key: 'id',
        render: (id: number) => {
          return (
            <a
              href={`/pms/product-view/${id}`}
              target="_blank"
              rel="noreferrer"
            >
              {id}
            </a>
          );
        },
      },
      {
        title: i18n.t(LOCALS.SYLYoWTeQq),
        dataIndex: 'description',
        key: 'description',
        render: (description: string) => {
          return description || '-';
        },
      },
      {
        dataIndex: 'pic',
        key: 'pic',
        title: <Trans i18nKey={LOCALS.product_pictures} />,
        render: (pic: string) => {
          if (!pic) return <span>-</span>;

          return <img src={pic} width={100} height={100} alt={pic} />;
        },
      },
      {
        dataIndex: 'name',
        key: 'name',
        width: 120,
        title: <Trans i18nKey={LOCALS.product_name} />,
      },
      {
        dataIndex: 'price',
        key: 'price',
        title: <Trans i18nKey={LOCALS.price} />,
        render: (price: number, product: PmsProductWithPriceType) => {
          const { id } = product;

          const omsOrderItem = orderResult?.omsOrderItems.find(
            (i) => i.productId === id
          );

          if (!omsOrderItem) return null;
          return (
            <div>
              {omsOrderItem.actualCurrency}{' '}
              {omsOrderItem.productPriceActualCurrency.toLocaleString()}
            </div>
          );
        },
      },
      {
        dataIndex: 'stockStatus',
        key: 'stockStatus',
        title: i18n.t(LOCALS.uMihwkqqts),
        render(stockStatus: PmsProductWithPriceType['stockStatus']) {
          return (
            <div>
              {findLabelByValue(
                stockStatus,
                PMS_PRODUCT_STOCK_STATUS_OPTION_LIST
              )}
            </div>
          );
        },
      },
      {
        dataIndex: 'publish_status',
        key: 'publish_status',
        title: (
          <div>
            <Trans i18nKey={LOCALS.publish_status} /> /{' '}
            <Trans i18nKey={LOCALS.stock} />
          </div>
        ),
        render: (_, { publishStatus, stock }) => {
          return (
            <div>
              {findLabelByValue(publishStatus, PUBLISH_STATUS_OPTION_LIST)} /{' '}
              {stock}
            </div>
          );
        },
      },
      {
        dataIndex: 'stockPlace',
        key: 'stockPlace',
        title: <Trans i18nKey={LOCALS.stock_place} />,
        render: (stockPlace: PmsProductWithPriceType['stockPlace']) => {
          return findLabelByValue(stockPlace, STOCK_PLACE_OPTION_LIST);
        },
      },
    ];
    if (mode === 'list') {
      columns = [
        ...columns,
        {
          title: i18n.t(LOCALS.price_type),
          key: 'priceType',
          render: (product: PmsProductWithPriceType) => {
            const { priceType } = product;

            return (
              <Select
                className="w-full"
                value={priceType}
                defaultValue={CHECKOUT_OUT_PRODUCT_PRICE_TYPE.WITH_OUT_TAX}
                options={CHECKOUT_OUT_PRODUCT_PRICE_TYPE_OPTION_LIST}
                onChange={(value) => {
                  const newProduct = {
                    ...product,
                    priceType: value,
                  };
                  onChange?.(newProduct);
                }}
              ></Select>
            );
          },
        },
        {
          title: i18n.t('quantity'),
          key: 'quantity',
          width: 150,
          render: (product: PmsProductWithPriceType) => {
            const { quantity, skuType } = product;

            return (
              <InputNumber
                disabled={skuType === PRODUCT_STOCK_TYPE.SINGLE}
                className="w-full"
                value={quantity}
                min={1}
                max={product.stock}
                onChange={(value) => {
                  if (!value) return;
                  const newProduct = {
                    ...product,
                    quantity: value,
                  };
                  onChange?.(newProduct);
                }}
              />
            );
          },
        },
        {
          title: i18n.t(LOCALS.option),
          key: 'option',
          render: (product: PmsProductWithPriceType) => {
            return (
              <div>
                <Button
                  type="link"
                  onClick={() => {
                    window.open(`/pms/product-edit/${product.id}`);
                  }}
                >
                  {i18n.t(LOCALS.details)}
                </Button>
                <Button
                  type="link"
                  onClick={() => {
                    onDelete?.(product);
                  }}
                >
                  {i18n.t(LOCALS.delete)}
                </Button>
              </div>
            );
          },
        },
      ];
    }
    return columns;
  }, [mode, onChange, onDelete, orderResult?.omsOrderItems]);

  return (
    <Table
      size="small"
      loading={loading}
      className="mt-4"
      bordered
      pagination={false}
      columns={getColumns}
      dataSource={productList}
      rowKey={'id'}
      rowSelection={
        mode === 'list'
          ? undefined
          : {
              type: 'radio',
              onChange: (
                _: React.Key[],
                selectedRows: PmsProductWithPriceType[]
              ) => {
                onChange?.(selectedRows[0]);
              },
            }
      }
    ></Table>
  );
};

export default ProductTable;
